import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { auth } from '../config/firebase';
import app from '../config/firebase';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { state } = useAppContext();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const db = getFirestore(app);

  useEffect(() => {
    const verifyAdminAccess = async () => {
      try {
        // First check if user is authenticated
        if (!state.isAuthenticated || !auth.currentUser) {
          setIsAuthorized(false);
          setIsVerifying(false);
          return;
        }

        // Verify that the authenticated user exists in adminUsers collection
        const adminEmail = auth.currentUser.email;
        if (!adminEmail) {
          console.error('❌ No email found for authenticated user');
          setIsAuthorized(false);
          setIsVerifying(false);
          return;
        }

        const adminsRef = collection(db, 'adminUsers');
        const q = query(adminsRef, where('email', '==', adminEmail));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          console.error('❌ User not found in adminUsers collection:', adminEmail);
          setIsAuthorized(false);
        } else {
          console.log('✅ Admin access verified for:', adminEmail);
          setIsAuthorized(true);
        }
      } catch (error) {
        console.error('❌ Error verifying admin access:', error);
        setIsAuthorized(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAdminAccess();
  }, [state.isAuthenticated, db]);

  // Show loading while verifying
  if (isVerifying) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated or not authorized
  if (!state.isAuthenticated || !isAuthorized) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;